import { Button, ButtonVariant, ChangeEvent, ClickEvent, Component, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, DropdownItem, Label, LookupModelSearchEvent, LookupModelSelectionEvent, Panel, Snackbar, TableRow, Textbox, Toast } from "@mcleod/components";
import { Api, CompanySettings, HorizontalAlignment, McLeodClassicIntegration, Model, ModelRow, Navigation, StringUtil, UserSettings, UserType } from "@mcleod/core";
import { DuplicateId, DuplicateIdProps } from "@mcleod/dispatch";
import { ImageName } from "@mcleod/images";
import { AssetUtil } from "./AssetUtil";
import { AutogenLayoutWebUser } from "./autogen/AutogenLayoutWebUser";
import { LMECompanySettings } from "./settings/LMECompanySettings";

const viewWebUserURL = "lme/general/WebUser?mode=update&key=";
const webAcctStatusEnabledLocked = [ new DropdownItem("E", "Enabled"), new DropdownItem("L", "Locked") ];
const webAcctStatusLockedWaiting = [ new DropdownItem("L", "Locked"), new DropdownItem("W", "Waiting") ];
const webAcctStatusLocked = [ new DropdownItem("L", "Locked") ];

const DUPLICATE_VALIDATION_PROPS_ID: DuplicateIdProps = {
    tableName: "users",
    screenName: "Web User",
    navigateToUrl: viewWebUserURL
};

const DUPLICATE_VALIDATION_PROPS_LOGIN_ID: DuplicateIdProps = {
    tableName: "users",
    screenName: "Web User",
    navigateToUrl: viewWebUserURL,
    altFieldNameForDupCheck: "login_id",
    primaryKeyFieldNameForAlt: "id"
}

const COMPANY_TYPE_BOTH = "L";
const COMPANY_TYPE_BROKERAGE = "B";
const WEB_USER_TYPE_CUSTOMER = "C";

export class WebUser extends AutogenLayoutWebUser {
    codeGenerationIsActive: boolean;

    override onLoad() {
        const userType = this.textboxWebUserType.selectedItem?.value;
        this.updateSourceNameLookup(this.textboxTypeNumber, userType);
        this.textboxWebAcctStatus.enabledDuringAdd = false;

        DuplicateId.setupValidateOnBlur(this.textboxId, DUPLICATE_VALIDATION_PROPS_ID);
        DuplicateId.setupValidateOnBlur(this.textboxLoginId, DUPLICATE_VALIDATION_PROPS_LOGIN_ID);

        this.textboxWebUserType.addChangeListener((event: ChangeEvent) => {
            if ("Driver" == event.oldValue) {
                if (event.userInitiatedChange) {
                    this.mainDataSource.activeRow?.setLookupModelData("payee_id", { name: "", id: "" });
                }
                this.mainDataSource.displayDataInBoundComponent(this.textboxPayeeId);
                this.textboxTypeNumber.removeLookupModelSelectionListener((event: LookupModelSelectionEvent) => this.driverOnLookupModelSelection(event));
            }
            if ("Driver" == event.newValue) {
                if (event.userInitiatedChange) {
                    this.mainDataSource.activeRow?.setLookupModelData("payee_id", { name: "", id: "" });
                }
                this.mainDataSource.displayDataInBoundComponent(this.textboxPayeeId);
                this.textboxTypeNumber.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => this.driverOnLookupModelSelection(event));
            }
            this.panelFactoringCompanyFields.visible = event.newValue == "Factoring Company";
            this.panelAssociatedLocations.visible = event.newValue == "Factoring Company";

            this.textboxTypeNumber.clear();
            const portalAdmin = this.mainDataSource.activeRow?.getString("portal_admin_id");
            if (!portalAdmin || portalAdmin != UserSettings.getUserId()) {
                this.textboxContactName.clear();
            }
            this.setDefaultUserGroup(this.textboxWebUserType.selectedItem?.value);
            this.updateSourceNameLookupWithDropdownItems(this.textboxTypeNumber, this.textboxWebUserType.selectedItems);
            this.textboxHdrSourceName.caption = this.textboxWebUserType.selectedItems?.length == 1 ? this.textboxWebUserType.text : "User Name";
            this.textboxHdrSourceName.text = this.textboxTypeNumber.text;
        });

        this.textboxTypeNumber.addChangeListener((event: ChangeEvent) => {
            const portalAdmin = this.mainDataSource.activeRow?.getString("portal_admin_id");
            if ((!portalAdmin || portalAdmin != UserSettings.getUserId()) && event.userInitiatedChange) {
                this.textboxContactName.clear();
            }
            this.textboxHdrSourceName.text = this.textboxTypeNumber.text;
        });

        this.textboxContactName.addBeforeLookupModelSearchListener((event: LookupModelSearchEvent) => {
            const parentRowType = this.activeRow?.get("web_user_type");
            const parentRowId = this.activeRow?.get("type_number");
            if (parentRowType == null || parentRowId == null) {
                event.preventDefault();
            } else {
                // Carrier contacts can are type "P", but carrier users use "R"
                // All other types match between contacts and users
                event.filter.parent_row_type = parentRowType === "R" ? "P" : parentRowType;
                event.filter.parent_row_id = parentRowId;
                if (this.mainDataSource.mode === DataSourceMode.ADD) {
                    event.filter.web_access = 'N';
                }
            }
        });

        this.mainDataSource.addDisplayListener(() => {
            if (this.mainDataSource.activeRow?.get("name") != null) {
                this.mainDataSource.activeRow?.setLookupModelData("name", {
                    "name": this.mainDataSource?.activeRow.get("name", "")
                });
            }
            if (this.mainDataSource.activeRow?.get("portal_admin_id") != null) {
                this.mainDataSource.activeRow?.setLookupModelData("portal_admin_id", {
                    "id": this.mainDataSource?.activeRow.get("portal_admin_id", "")
                });
            }
        });

        this.sourceWebUsers.doBeforeDataSourcePost = async () => {
            if (this.mainDataSource.mode === DataSourceMode.ADD) {
                if (StringUtil.isEmptyString(this.textboxLoginId.text)) {
                    this.textboxLoginId.text = this.textboxEmailAddress.text;
                }
                if (!await DuplicateId.validateBeforePost(this.textboxLoginId, DUPLICATE_VALIDATION_PROPS_LOGIN_ID)) {
                    return false;
                }

                if (!await DuplicateId.validateBeforePost(this.textboxId, {
                    ...DUPLICATE_VALIDATION_PROPS_ID,
                    codeGenerationIsActive: this.codeGenerationIsActive
                })) {
                    return false;
                }

                const contactExists = await this.checkForExistingContact();
                return !contactExists;
            } else {
                return true;
            }
        }
    }

    textboxContactNameOnLookupModelSelection(event: LookupModelSelectionEvent) {
        const contactRow = event.selection;
        if (contactRow) {
            this.activeRow?.set("email_address", contactRow.getString("email", null));
            this.activeRow?.set("phone", contactRow.get("phone", null));
        }
    }

    async checkForExistingContact(): Promise<boolean> {
        const contactName = this.mainDataSource.activeRow?.getString("name", null);
        const userType = this.mainDataSource.activeRow?.getString("web_user_type", null);
        const typeNumber = this.mainDataSource.activeRow?.getString("type_number", null);
        if (this.validateSimple(true, false)) {
            const contactId: string = this.textboxContactName.getFirstLookupModelData()?.getString("id", null);
            const result = await Api.post("lme/general/web-users-contact", {
                "contact_id": contactId,
                "user_type": userType === "R" ? "P" : userType,
                "type_number": typeNumber,
                "contact_name": contactName
            });

            const data = result?.data[0];
            const contactExists = data?.contact_exists as boolean;
            const userId = data?.user_id as string;
            if (contactExists) {
                if (userId != null) {
                    const snackBar = this.getExistingUserSnackbarPanel(contactName, this.textboxTypeNumber.text, userId);
                    Snackbar.showWarningSnackbar(snackBar, { persist: true, warningTitle: "Web User Already Exists" });
                    this.textboxContactName.focus();
                    return true;
                } else {
                    Snackbar.showSnackbar("Creating web user for existing contact [" + contactName + " - " + this.textboxTypeNumber.text + "].");
                    return false;
                }
            } else {
                Snackbar.showSnackbar("[" + contactName + "] is not an existing contact for " + this.textboxTypeNumber.text + ". A new contact will be created.");
                return false;
            }
        }
    }

    private getExistingUserSnackbarPanel(contactName: string, sourceName: string, userId: string): Panel {
        const labelProps = { color: "white", fontSize: "large", rowBreak: false, paddingLeft: 1, paddingRight: 1, marginLeft: 0, marginRight: 0 };

        let components: Component[] = [];
        if (!StringUtil.isEmptyString(contactName)) {
            components = [
                new Label({ ...labelProps, text: "[" + contactName + " - " + sourceName }),
                new Button({
                    imageName: ImageName.detail,
                    imageWidth: 20,
                    imageHeight: 20,
                    margin: 0,
                    tooltip: "Click to go to Web User screen",
                    color: "subtle.light",
                    variant: ButtonVariant.round,
                    onClick: () => this.existingUserLinkOnClick(userId),
                    rowBreak: false
                }),
                new Label({ ...labelProps, rowBreak: true, text: "] is already a web user." }),
                new Label({ ...labelProps, text: "A new user was not created." })
            ];
        }
        return new Panel({ wrap: true, align: HorizontalAlignment.LEFT, components: components, fillHeight: true });
    }

    private existingUserLinkOnClick(userId: string) {
        Navigation.navigateTo(viewWebUserURL + userId, { newTab: true });

        this.disableResetButton();
    }

    disableResetButton() {
        this.buttonReset.enabled = false;
        this.buttonReset.backgroundColor = "primary.reverse";
        this.buttonReset.color = "McLeodDisable";
        this.buttonReset.borderColor = "McLeodDisable";
    }

    onDataSourceDisplay(event) {
        const userName: string = this.mainDataSource.activeRow?.get("source_name", "");
        const typeNumber = this.mainDataSource.activeRow?.get("type_number", "");
        const webUserType = this.mainDataSource.activeRow?.get("web_user_type", "");
        const portalAdmin = this.mainDataSource.activeRow?.getString("portal_admin_id");
        if (portalAdmin && portalAdmin == UserSettings.getUserId()) {
            this.textboxWebUserType.enabledDuringUpdate = true;
            this.textboxTypeNumber.enabledDuringUpdate = true;
        }
        this.updateSourceNameLookup(this.textboxTypeNumber, webUserType);
        const lookupModelRow = new ModelRow(this.textboxTypeNumber.lookupModel, false, { id: typeNumber, name: userName });
        this.mainDataSource.activeRow?.setLookupModelData("type_number", lookupModelRow);
        if (this.mainDataSource.activeRow?.get("id", null) != null) {
            this.buttonReset.enabled = true;
            this.buttonReset.backgroundColor = "primary";
            this.buttonReset.color = "primary.reverse";
            this.buttonReset.borderColor = "primary";
        }
        this.textboxHdrSourceName.caption = this.textboxWebUserType.text;
        this.updateWebAcctStatusDropdown(this.mainDataSource.activeRow?.getString("web_acct_status", null));
        this.buttonOpenClassic.visible = this.mainDataSource.activeRow?.get("id", null) != null;
        const isUserType = WEB_USER_TYPE_CUSTOMER === this.mainDataSource.activeRow?.get("web_user_type");
        this.textboxAllowWebTendering.visible = this.isCompanyBothLTLAndBrokerage() && isUserType;
    }

    textboxWebUserTypeOnChange(event: ChangeEvent) {
        const selectedItems: DropdownItem[] = this.textboxWebUserType.selectedItems;
        const selectedValues: string[] = selectedItems.map(item => item.value) ?? [];
        this.textboxAllowWebTendering.visible = this.isCompanyBothLTLAndBrokerage() && selectedValues.includes(WEB_USER_TYPE_CUSTOMER);
    }

    isCompanyBothLTLAndBrokerage(): boolean {
        const companyType = CompanySettings.get().company_type;
        const isBrokerageLTL = CompanySettings.get().is_brokerage_ltl;
        return [COMPANY_TYPE_BOTH, COMPANY_TYPE_BROKERAGE].includes(companyType) && isBrokerageLTL;
    }

    sourceWebUsersAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() === DataSourceAction.UPDATE) {
            this.updateWebAcctStatusDropdown(this.mainDataSource.activeRow?.getString("web_acct_status", null));
        }
    }

    sourceWebUsersBeforeModeChange(event) {
        if (event.newMode === DataSourceMode.ADD) {
            Api.post("lme/general/code-generation-settings", { "table_name": "users" }).then(response => {
                this.codeGenerationIsActive = response.data[0]?.is_active ?? false;
            });
        }
    }

    updateWebAcctStatusDropdown(webAcctStatus: string) {
        if (this.mainDataSource.mode === DataSourceMode.UPDATE) {
            if (webAcctStatus == "E") {
                this.textboxWebAcctStatus.items = webAcctStatusEnabledLocked;
            } else if (webAcctStatus == "W") {
                this.textboxWebAcctStatus.items = webAcctStatusLockedWaiting;
            } else {
                this.textboxWebAcctStatus.items = webAcctStatusLocked;
            }
        }
    }

    buttonOpenClassicOnClick(event: ClickEvent) {
        McLeodClassicIntegration.openClassicScreen("com.tms.client.loadmaster.web.EntryWebUser", this.mainDataSource.activeRow.get("id"));
    }

    private setDefaultUserGroup(value: string) {
        let defUserGroup: string;
        switch (value) {
            case "R": // Carrier
                defUserGroup = LMECompanySettings.get().carrier_user_group;
                break;
            case "C": // Customer
                defUserGroup = LMECompanySettings.get().customer_user_group;
                break;
            case "D": // Driver
                defUserGroup = LMECompanySettings.get().driver_user_group;
                break;
            case "L": // Location
                defUserGroup = LMECompanySettings.get().location_user_group;
                break;
            default:
                defUserGroup = "";
        }
        if (defUserGroup) {
            Model.searchSingleRecord(this.textboxUserGroup.lookupModel, {
                id: defUserGroup
            }).then((record) => {
                this.mainDataSource.activeRow?.setLookupModelData("user_group", record);
                this.mainDataSource.activeRow?.set("user_group", record?.get("id"));
                this.mainDataSource.displayDataInBoundComponent(this.textboxUserGroup);
            });
        }
        else {
            this.mainDataSource.activeRow?.setLookupModelData("user_group", { descr: "", id: "" });
            this.mainDataSource.displayDataInBoundComponent(this.textboxUserGroup);
        }
    }

    private driverOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection() && "D" == this.textboxWebUserType.selectedItem?.value &&
            !event.selection.isNull("payee_id")) {
            Model.searchSingleRecord(this.textboxPayeeId.lookupModel, {
                id: event.selection.get("payee_id")
            }).then((record) => {
                this.mainDataSource.activeRow?.setLookupModelData("payee_id", record);
                this.mainDataSource.activeRow?.set("payee_id", record?.get("name"));
                this.mainDataSource.displayDataInBoundComponent(this.textboxPayeeId);
            });
        }
        else {
            this.mainDataSource.activeRow?.setLookupModelData("payee_id", { name: "", id: "" });
            this.mainDataSource.displayDataInBoundComponent(this.textboxPayeeId);
        }
    }

    private updateSourceNameLookupWithDropdownItems(textboxTypeNumber: Textbox, selectedItems: DropdownItem[]) {
        if (selectedItems?.length == 1) {
            this.updateSourceNameLookup(textboxTypeNumber, selectedItems[0].value);
        } else {
            textboxTypeNumber.caption = "User Name";
            textboxTypeNumber.lookupModelDisabled = true;
            textboxTypeNumber.enabledDuringSearch = false;
        }
    }

    private updateSourceNameLookup(textboxTypeNumber: Textbox, value: string) {

        textboxTypeNumber.lookupModelDisabled = false;
        textboxTypeNumber.enabledDuringSearch = true;

        switch (value) {
            case "R": // Carrier
                textboxTypeNumber.caption = "Carrier Name";
                textboxTypeNumber.lookupModel = "lme/powerbroker/carriers";
                textboxTypeNumber.lookupModelLayout = "lme/powerbroker/CarriersForAssignment";
                textboxTypeNumber.lookupModelResultField = "id";
                textboxTypeNumber.lookupModelDisplayField = "name";
                break;
            case "C": // Customer
                textboxTypeNumber.caption = "Customer Name";
                textboxTypeNumber.lookupModel = "lme/ar/customer";
                textboxTypeNumber.lookupModelLayout = "lme/ar/CustomerLookup";
                textboxTypeNumber.lookupModelResultField = "id";
                textboxTypeNumber.lookupModelDisplayField = "name";
                break;
            case "D": // Driver
                textboxTypeNumber.setProps({
                    caption: "Driver Name",
                    ...AssetUtil.getDriverLookupModelProps(),
                    lookupModelExtraFieldList: "lme/dispatch/asset/driver-web-user"
                });
                break;
            case "5": // Factoring Company
                textboxTypeNumber.caption = "Factoring Company Name";
                textboxTypeNumber.lookupModel = "lme/settlement/factoring-company-lookup";
                textboxTypeNumber.lookupModelLayout = "lme/dispatch/FactoringCompanyLookup";
                textboxTypeNumber.lookupModelResultField = "id";
                textboxTypeNumber.lookupModelDisplayField = "name";
                break;
            case "L": // Location
                textboxTypeNumber.caption = "Location Name";
                textboxTypeNumber.lookupModel = "lme/dispatch/location-lookup";
                textboxTypeNumber.lookupModelLayout = "lme/dispatch/LocationLookup";
                textboxTypeNumber.lookupModelResultField = "id";
                textboxTypeNumber.lookupModelDisplayField = "name";
                break;
            default:
                textboxTypeNumber.caption = "User Name";
                textboxTypeNumber.lookupModelDisabled = true;
                textboxTypeNumber.enabledDuringSearch = false;
                break;
        }
    }

    textboxFileimportProfileBeforeLookupModelSearch(event) {
        event.filter.is_active = 'Y';
        event.filter.data_type = 'A';
    }

    textboxPortalAdminIdBeforeLookupModelSearch(event) {
        event.filter.web_user_type = UserType.LME;
    }

    buttonResetPassword() {
        this.disableResetButton();
        this.resetPortalPassword();
    }

    async resetPortalPassword() {
        const userId = this.mainDataSource.activeRow?.getString("id", null);
        if (userId) {
            const result = await Api.post("lme/general/reset-portal-password", {
                "user_id": userId
            });

            const emailSuccess = result?.data[0]?.success;
            if (emailSuccess) {
                setTimeout(() => Toast.showToast(`Password reset email successfully sent`), 500);
                this.buttonReset.caption = "Password Email Sent";
                this.buttonReset.enabled = false;
                this.buttonReset.backgroundColor = "success";
                this.buttonReset.color = "success.reverse";
                this.buttonReset.borderColor = "success";
            } else {
                this.disableResetButton();
                Snackbar.showWarningSnackbar("There was an unexpected error resetting the password");
            }

            Model.searchSingleRecord(this.mainDataSource.url, {
                id: this.mainDataSource.activeRow.get("id")
            }).then((record) => {
                this.updateWebAcctStatusDropdown(record?.getString("web_acct_status", null));
                this.textboxWebAcctStatus.displayComponentData(record, null, 0);
            });
        }
    }


    textboxLocationIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection()) {
            const textbox = event.target as Textbox;
            const tableRow = TableRow.getContainingTableRow(textbox);
            tableRow.data.set("state", event.selection.get("state"));
            tableRow.data.set("city_name", event.selection.get("city_name"));
            tableRow.data.set("name", event.selection.get("name"));
            tableRow.displayComponentValues();
        }
    }
}
